import { Skeleton } from 'antd';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import styles from './PieChart.module.css';
import { useResize } from '@/shared/utils';

const RADIAN = Math.PI / 180;

const COLORS_LIGHT = [
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#8884D8',
  '#82CA9D',
  '#FF6B6B',
  '#4ECDC4',
  '#45B7D1',
  '#A05195',
];
const COLORS_DARK = [
  '#3f51b5',
  '#4caf50',
  '#f44336',
  '#ff9800',
  '#9c27b0',
  '#607d8b',
  '#e91e63',
  '#8bc34a',
  '#2196f3',
  '#795548',
];

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.75;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill='white'
      textAnchor='middle'
      dominantBaseline='central'
      style={{
        fontSize: '12px',
        fontWeight: 'bold',
      }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export const CustomPieChart = ({ data, title, darkMode = false, outerRadius = 120, handleCellClick = () => {} }) => {
  const { isMobile } = useResize();

  const colors = darkMode ? COLORS_DARK : COLORS_LIGHT;

  const processedData = processChartData(data);

  const chartData = processedData?.map((item, index) => ({
    ...item,
    color: colors[index % colors.length],
    percent: item.value / processedData.reduce((sum, d) => sum + d.value, 0),
  }));

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: isMobile ? 500 : 400,
        padding: '16px',
      }}
    >
      <h3 className={styles.heading}>{title}</h3>

      <ResponsiveContainer width='100%' height={isMobile ? '70%' : '90%'}>
        {data ? (
          <PieChart>
            <Pie
              data={chartData}
              cx='50%'
              cy='50%'
              outerRadius={outerRadius}
              dataKey='value'
              labelLine={false}
              label={renderCustomizedLabel}
              animationBegin={100}
              animationDuration={1000}
              animationEasing='ease-out'
            >
              {chartData?.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  fillOpacity={0.6}
                  stroke={entry.color}
                  strokeWidth={1}
                  onClick={() => {
                    entry.name !== 'Другое' && handleCellClick(entry.name);
                  }}
                />
              ))}
            </Pie>
            <Tooltip />

            <Legend
              layout={isMobile ? 'horizontal' : 'vertical'}
              verticalAlign={isMobile ? 'bottom' : 'middle'}
              align={isMobile ? 'center' : 'right'}
              wrapperStyle={{
                padding: '10px',
                marginTop: isMobile ? '20px' : 0,
              }}
              formatter={(value, entry, index) => (
                <span style={{ color: 'var(--primary)', fontSize: isMobile ? '12px' : 'inherit' }}>
                  {`${value} (${(chartData[index]?.percent * 100).toFixed(1)}%)`}
                </span>
              )}
            />
          </PieChart>
        ) : (
          <div className={styles.skeletonWrapper}>
            <Skeleton.Node active style={{ width: '100%', height: '100%' }}>
              <></>
            </Skeleton.Node>
          </div>
        )}
      </ResponsiveContainer>
    </div>
  );
};

export const processChartData = (rawData, emptyMessage = 'Данные отсутствуют') => {
  if (!rawData?.length) {
    return [{ id: 0, value: 100, name: emptyMessage }];
  }

  const sorted = [...rawData].sort((a, b) => Number(b.value) - Number(a.value));

  return sorted.length > 10
    ? [
        ...sorted.slice(0, 10),
        {
          id: 'other',
          value: sorted.slice(10).reduce((sum, item) => sum + Number(item.value), 0),
          name: 'Другое',
        },
      ]
    : sorted;
};
